import { type FC, useMemo } from 'react'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { FilterChip } from '../../../components/CustomWorkflows/tags/FilterChip'
import { ENTITY_TAGS, ENTITY_TAG_VALUES, entityTag } from '../../../components/CustomWorkflows/entityTags'
import { useListCustomWorkflowsQuery } from '../../../store/customWorkflow/customWorkflows'
import { type RunFiltersValue } from './runFilterParams'
import { WORKFLOW_LOOKUP_SIZE } from './useWorkflowsById'
import './RunFilters.scss'
const ALL_AREAS = 'All areas'
const ALL_WORKFLOWS = 'All workflows'
const DROPDOWN_CLASS = 'zocks-py-0.375 zocks-px-0.75 rounded-2 bg-white zocks-border-color-gray-200 paragraph-small'

interface RunAreaFilterProps {
  value: RunFiltersValue
  onChange: (value: RunFiltersValue) => void
}

interface RunWorkflowFilterProps {
  accountId: string
  value: RunFiltersValue
  onChange: (value: RunFiltersValue) => void
}

export const RunAreaFilter: FC<RunAreaFilterProps> = ({ value, onChange }) => {
  return (
    <div className='d-flex flex-wrap gap-1' role='group' aria-label='Filter runs by area'>
      <FilterChip pressed={value.entity === null} label={ALL_AREAS} onClick={() => onChange({ ...value, entity: null })} />
      {ENTITY_TAG_VALUES.map((entity) => (
        <FilterChip
          key={entity}
          pressed={value.entity === entity}
          label={ENTITY_TAGS[entity].chipLabel}
          icon={ENTITY_TAGS[entity].icon}
          onClick={() => onChange({ workflowId: null, entity })}
        />
      ))}
    </div>
  )
}

export const RunWorkflowFilter: FC<RunWorkflowFilterProps> = ({ accountId, value, onChange }) => {
  const {
    currentData: workflows,
    isFetching,
    isError,
  } = useListCustomWorkflowsQuery({ accountId, size: WORKFLOW_LOOKUP_SIZE, ...(value.entity ? { scope: entityTag(value.entity) } : {}) }, { skip: !accountId })
  const listIsSettled = !isFetching && !isError && workflows !== undefined
  const listIsWhole = listIsSettled && workflows.length < WORKFLOW_LOOKUP_SIZE
  const absenceMeansDeleted = listIsWhole && !value.entity

  const workflowOptions = useMemo<SelectOption[]>(
    () => (workflows ?? []).filter((workflow) => workflow.triggerType !== 'hook').map((workflow) => ({ id: workflow.id, label: workflow.name })),
    [workflows],
  )
  const selectedWorkflow = useMemo<SelectOption | null>(() => {
    if (!value.workflowId) {
      return null
    }
    const listed = workflowOptions.find((option) => option.id === value.workflowId)
    return listed ?? { id: value.workflowId, label: absenceMeansDeleted ? 'Unavailable workflow' : 'Selected workflow' }
  }, [value.workflowId, workflowOptions, absenceMeansDeleted])

  return (
    <div className='run-filters d-flex flex-wrap align-items-center zocks-gap-1'>
      <SelectDropdown
        ariaLabel='Filter runs by workflow'
        toggleId='runFilterWorkflow'
        className={DROPDOWN_CLASS}
        placeholder={ALL_WORKFLOWS}
        options={workflowOptions}
        initialSelectedOption={selectedWorkflow}
        loading={isFetching}
        disabled={workflowOptions.length === 0 && listIsSettled && !value.workflowId}
        onOptionSelected={(option) => onChange({ ...value, workflowId: option.id })}
        showDeselectOption
        deselectOptionLabel={ALL_WORKFLOWS}
        onDeselect={() => onChange({ ...value, workflowId: null })}
      />
    </div>
  )
}
